/**
 * Story decorator that removes hardcoded ThemeProvider from component output
 *
 * WHY THIS EXISTS:
 * Some components (like AccountCreateScreen) have hardcoded <ThemeProvider theme={rewardsTheme}>
 * that overrides the global brand theme in Storybook. This decorator removes the internal
 * ThemeProvider so the global brand theme decorator can work.
 *
 * HOW IT WORKS:
 * 1. Renders the story component
 * 2. Finds ThemeProvider in the React element tree
 * 3. Extracts and renders its children directly, bypassing the hardcoded theme
 */
import React from 'react';
import {ThemeProvider} from '@rn-vui/themed';
import type {DecoratorFunction} from '@storybook/types';

/**
 * Recursively find and unwrap ThemeProvider from React element tree
 */
function unwrapThemeProvider(element: React.ReactElement): React.ReactElement {
  // Check if this element is a ThemeProvider
  if (element.type === ThemeProvider) {
    // Return its children directly, bypassing the ThemeProvider
    return element.props.children;
  }

  // If element has children, recursively process them
  if (element.props && element.props.children) {
    const children = React.Children.map(element.props.children, child => {
      if (React.isValidElement(child)) {
        return unwrapThemeProvider(child);
      }
      return child;
    });

    // Clone the element with unwrapped children
    return React.cloneElement(element, {}, children);
  }

  // No ThemeProvider found, return as-is
  return element;
}

/**
 * Decorator that removes internal ThemeProvider from rendered component
 * Apply this to stories that have hardcoded ThemeProvider
 */
export const RemoveInternalThemeProvider: DecoratorFunction = Story => {
  const rendered = <Story />;

  // Only process if it's a valid React element
  if (!React.isValidElement(rendered)) {
    return rendered;
  }

  // Unwrap any ThemeProvider in the tree
  return unwrapThemeProvider(rendered);
};
